/**
 * Site configuration React hook — reads /api/site/config and caches in memory.
 *
 * Usage : ``const cfg = useSiteConfig();`` — returns ``null`` until ready,
 * then the merged object with keys hero/univers/offers/contact/footer/instagram.
 */
import { useEffect, useState } from "react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

let cache = null;
let inFlight = null;
const listeners = new Set();

async function loadConfig() {
  if (cache) return cache;
  if (inFlight) return inFlight;
  inFlight = fetch(`${BACKEND}/api/site/config`)
    .then((r) => r.json())
    .then((d) => {
      cache = d || {};
      listeners.forEach((cb) => cb(cache));
      return cache;
    })
    .catch(() => {
      cache = {};
      return cache;
    })
    .finally(() => { inFlight = null; });
  return inFlight;
}

export function invalidateSiteConfig() {
  cache = null;
}

export function useSiteConfig() {
  const [cfg, setCfg] = useState(cache);
  useEffect(() => {
    let mounted = true;
    loadConfig().then((d) => { if (mounted) setCfg(d); });
    const cb = (d) => { if (mounted) setCfg(d); };
    listeners.add(cb);
    return () => { mounted = false; listeners.delete(cb); };
  }, []);
  return cfg;
}

/* Convenience selectors with sensible defaults */
export const sel = {
  hero: (cfg) => cfg?.hero || {},
  univers: (cfg) => cfg?.univers || { items: [] },
  offers: (cfg) => cfg?.offers || {},
  contact: (cfg) => cfg?.contact || {},
  footer: (cfg) => cfg?.footer || {},
  instagram: (cfg) => cfg?.instagram || { posts: [] },
  tracking: (cfg) => cfg?.tracking || {},
  faq: (cfg) => cfg?.faq || { section_title: "Questions fréquentes", items: [] },
  testimonials: (cfg) => cfg?.testimonials || { section_title: "Ils en parlent", items: [] },
  mentionsLegales: (cfg) => cfg?.mentions_legales || {},
  menusNav: (cfg) => cfg?.menus_nav || { primary: [], cta_label: "", cta_to: "" },
  seo: (cfg) => cfg?.seo || { default: {}, pages: {} },
  // iter-62 — new dynamic sections
  experiencesChoice: (cfg) => cfg?.experiences_choice || { kicker: "", title: "", subtitle: "", cards: [] },
  founderWord: (cfg) => cfg?.founder_word || { enabled: true, kicker: "", title: "", paragraphs: [], signature_name: "", signature_role: "", image_url: "" },
  newsletterPopup: (cfg) => cfg?.newsletter_popup || { enabled: true, kicker: "", title: "", subtitle: "", cta_label: "S'inscrire", success_message: "", delay_ms: 8000 },
  comingSoon: (cfg) => cfg?.coming_soon || { title: "Très bientôt disponible", body: "", cta_primary: "Découvrir nos autres expériences", cta_secondary: "Fermer" },
  // iter-63 — page headers (immersive heros + home Le Kaai teaser)
  pageHeader: (cfg, key) => cfg?.[key] || {},
  // iter-77 — fiches techniques Hébergement (Chambre Exécutive & Suites)
  hebergementFiches: (cfg) => cfg?.hebergement_fiches || { amenities_catalog: [], rooms: [] },
};
